// ============ HELPERS: BUILD BINARY TREE FROM LEVEL-ORDER ARRAY ============
// null represented as -1 in input, or the string "null"

const buildTree = (arr) => {
  if (!arr || arr.length === 0) return null;
  const values = arr.map(v => (v === null || v === -1 || v === 'null') ? null : v);
  if (values[0] === null) return null;

  const root = { val: values[0], left: null, right: null, id: 0 };
  const queue = [root];
  let i = 1;
  let idCounter = 1;
  while (queue.length > 0 && i < values.length) {
    const node = queue.shift();
    if (i < values.length && values[i] !== null) {
      node.left = { val: values[i], left: null, right: null, id: idCounter++ };
      queue.push(node.left);
    }
    i++;
    if (i < values.length && values[i] !== null) {
      node.right = { val: values[i], left: null, right: null, id: idCounter++ };
      queue.push(node.right);
    }
    i++;
  }
  return root;
};

// Compute (x, y) for each node via inorder positioning
const computeLayout = (root) => {
  const positions = {};
  let counter = 0;
  const xSpacing = 60;
  const ySpacing = 80;
  const inorder = (node, depth) => {
    if (!node) return;
    inorder(node.left, depth + 1);
    positions[node.id] = { x: 40 + counter * xSpacing, y: 50 + depth * ySpacing };
    counter++;
    inorder(node.right, depth + 1);
  };
  inorder(root, 0);
  return { positions, width: Math.max(400, 40 + counter * xSpacing + 40) };
};

// Flatten tree into nodes and edges lists
const flattenTree = (root) => {
  const nodes = [];
  const edges = [];
  const walk = (node) => {
    if (!node) return;
    nodes.push({ id: node.id, val: node.val });
    if (node.left) { edges.push({ from: node.id, to: node.left.id }); walk(node.left); }
    if (node.right) { edges.push({ from: node.id, to: node.right.id }); walk(node.right); }
  };
  walk(root);
  return { nodes, edges };
};

const buildTreeContext = (arr) => {
  const root = buildTree(arr);
  if (!root) return null;
  const { positions, width } = computeLayout(root);
  const { nodes, edges } = flattenTree(root);
  return { root, positions, width, nodes, edges };
};

// ============ TREE 1: INORDER TRAVERSAL ============

export const generateInorderSteps = (arr) => {
  const steps = [];
  const ctx = buildTreeContext(arr);
  if (!ctx) return steps;
  const traversal = [];
  const visited = new Set();

  steps.push({
    line: 3,
    description: `Start inorder traversal (Left → Root → Right)`,
    treeCtx: ctx,
    visited: [],
    traversal: [],
    current: null,
    callStack: []
  });

  const inorder = (node, callStack) => {
    if (!node) return;
    const newStack = [...callStack, node.id];
    
    steps.push({
      line: 4,
      description: `Visit node ${node.val}, go LEFT first`,
      treeCtx: ctx,
      visited: [...visited],
      traversal: [...traversal],
      current: node.id,
      callStack: newStack
    });
    
    if (node.left) inorder({ ...node.left, id: node.left.id }, newStack);
    
    visited.add(node.id);
    traversal.push(node.val);
    steps.push({
      line: 6,
      description: `Process node ${node.val}. Traversal: [${traversal.join(', ')}]`,
      treeCtx: ctx,
      visited: [...visited],
      traversal: [...traversal],
      current: node.id,
      callStack: newStack,
      justVisited: node.id
    });
    
    if (node.right) inorder({ ...node.right, id: node.right.id }, newStack);
  };

  inorder(ctx.root, []);

  steps.push({
    line: 8,
    description: `Inorder complete: [${traversal.join(' → ')}]`,
    treeCtx: ctx,
    visited: [...visited],
    traversal: [...traversal],
    current: null,
    callStack: [],
    result: `[${traversal.join(', ')}]`
  });

  return steps;
};

// ============ TREE 2: LEVEL ORDER TRAVERSAL ============

export const generateLevelOrderSteps = (arr) => {
  const steps = [];
  const ctx = buildTreeContext(arr);
  if (!ctx) return steps;
  const traversal = [];
  const visited = new Set();
  const queue = [ctx.root];

  const idToNode = {};
  const idToLevel = {};
  const collect = (node, level) => {
    if (!node) return;
    idToNode[node.id] = node;
    idToLevel[node.id] = level;
    collect(node.left, level + 1);
    collect(node.right, level + 1);
  };
  collect(ctx.root, 0);

  steps.push({
    line: 3,
    description: `Start level-order (BFS) traversal. Add root to queue.`,
    treeCtx: ctx,
    visited: [],
    traversal: [],
    current: null,
    queue: [ctx.root.id]
  });

  const queueIds = [ctx.root.id];
  while (queueIds.length > 0) {
    const nodeId = queueIds.shift();
    const node = idToNode[nodeId];
    visited.add(nodeId);
    traversal.push(node.val);

    steps.push({
      line: 7,
      description: `Dequeue node ${node.val} (level ${idToLevel[nodeId]}). Add children.`,
      treeCtx: ctx,
      visited: [...visited],
      traversal: [...traversal],
      current: nodeId,
      queue: [...queueIds]
    });

    if (node.left) queueIds.push(node.left.id);
    if (node.right) queueIds.push(node.right.id);

    if (node.left || node.right) {
      steps.push({
        line: 10,
        description: `Enqueued children. Queue: [${queueIds.map(id => idToNode[id].val).join(', ')}]`,
        treeCtx: ctx,
        visited: [...visited],
        traversal: [...traversal],
        current: nodeId,
        queue: [...queueIds]
      });
    }
  }

  steps.push({
    line: 13,
    description: `Level order complete: [${traversal.join(' → ')}]`,
    treeCtx: ctx,
    visited: [...visited],
    traversal: [...traversal],
    current: null,
    queue: [],
    result: `[${traversal.join(', ')}]`
  });

  return steps;
};

// ============ TREE 3: LCA (Lowest Common Ancestor) ============

export const generateLCASteps = (input) => {
  // Parse "1,2,3,4,5,6,7|4|5"
  const parts = input.split('|').map(s => s.trim());
  const arr = parts[0].split(',').map(s => {
    const t = s.trim();
    if (t === 'null' || t === '-1') return null;
    const n = parseInt(t);
    return isNaN(n) ? null : n;
  });
  const p = parseInt(parts[1]);
  const q = parseInt(parts[2]);
  const ctx = buildTreeContext(arr);
  const steps = [];
  if (!ctx) return steps;

  const highlightP = new Set();
  const highlightQ = new Set();
  // Find node IDs for values p and q
  let pId = -1, qId = -1;
  for (const n of ctx.nodes) {
    if (n.val === p && pId === -1) pId = n.id;
    if (n.val === q && qId === -1) qId = n.id;
  }
  highlightP.add(pId);
  highlightQ.add(qId);

  steps.push({
    line: 3,
    description: `Find LCA of nodes with values ${p} and ${q}`,
    treeCtx: ctx,
    visited: [],
    current: null,
    pId,
    qId,
    p,
    q
  });

  // Build a map from id to node
  const idMap = {};
  const walk = (node) => {
    if (!node) return;
    idMap[node.id] = node;
    walk(node.left);
    walk(node.right);
  };
  walk(ctx.root);

  const visited = new Set();

  const lca = (node, depth = 0) => {
    if (!node) {
      steps.push({
        line: 5,
        description: `Reached null. Return null.`,
        treeCtx: ctx,
        visited: [...visited],
        current: null,
        pId, qId
      });
      return null;
    }

    visited.add(node.id);
    steps.push({
      line: 6,
      description: `Visit node ${node.val}. Check if it is p(${p}) or q(${q}).`,
      treeCtx: ctx,
      visited: [...visited],
      current: node.id,
      pId, qId
    });

    if (node.val === p || node.val === q) {
      steps.push({
        line: 7,
        description: `Found node ${node.val}! Return it up the recursion.`,
        treeCtx: ctx,
        visited: [...visited],
        current: node.id,
        pId, qId,
        found: node.id
      });
      return node;
    }

    const left = lca(node.left, depth + 1);
    const right = lca(node.right, depth + 1);

    if (left && right) {
      steps.push({
        line: 12,
        description: `Node ${node.val} has p in one subtree and q in other. This is the LCA!`,
        treeCtx: ctx,
        visited: [...visited],
        current: node.id,
        pId, qId,
        isLCA: node.id
      });
      return node;
    }

    steps.push({
      line: 14,
      description: `Node ${node.val}: pass up ${left ? left.val : right ? right.val : 'null'}`,
      treeCtx: ctx,
      visited: [...visited],
      current: node.id,
      pId, qId
    });
    return left || right;
  };

  const lcaNode = lca(ctx.root);

  steps.push({
    line: 17,
    description: `LCA of ${p} and ${q} = ${lcaNode ? lcaNode.val : 'null'}`,
    treeCtx: ctx,
    visited: [...visited],
    current: lcaNode ? lcaNode.id : null,
    pId, qId,
    isLCA: lcaNode ? lcaNode.id : null,
    result: lcaNode ? lcaNode.val : 'null'
  });

  return steps;
};

// ============ STRING 1: NAIVE PATTERN MATCHING ============

export const generateNaiveMatchSteps = (input) => {
  const parts = input.split('|').map(s => s.trim());
  const pattern = parts[0] || '';
  const text = parts[1] || '';
  const m = pattern.length;
  const n = text.length;
  const steps = [];
  const found = [];
  let comparisons = 0;

  steps.push({
    line: 2,
    description: `Search for "${pattern}" in "${text}"`,
    text, pattern,
    textIndex: -1,
    patternIndex: -1,
    comparisons: 0,
    found: [],
    algo: 'naive'
  });

  for (let i = 0; i <= n - m; i++) {
    let j = 0;
    steps.push({
      line: 4,
      description: `Align pattern at index ${i}`,
      text, pattern,
      textIndex: i,
      patternIndex: 0,
      comparisons,
      found: [...found],
      algo: 'naive'
    });

    while (j < m) {
      comparisons++;
      const match = text[i + j] === pattern[j];
      steps.push({
        line: 6,
        description: `Compare text[${i + j}]='${text[i + j]}' with pattern[${j}]='${pattern[j]}' → ${match ? 'match' : 'MISMATCH'}`,
        text, pattern,
        textIndex: i,
        patternIndex: j,
        comparisons,
        found: [...found],
        matchStatus: match ? 'match' : 'mismatch',
        algo: 'naive'
      });
      if (!match) break;
      j++;
    }

    if (j === m) {
      found.push(i);
      steps.push({
        line: 10,
        description: `Full match found at index ${i}!`,
        text, pattern,
        textIndex: i,
        patternIndex: m - 1,
        comparisons,
        found: [...found],
        matchStatus: 'found',
        algo: 'naive'
      });
    }
  }

  steps.push({
    line: 13,
    description: found.length > 0 
      ? `Complete! Found at indices: [${found.join(', ')}]. Comparisons: ${comparisons}`
      : `Complete! Pattern not found. Comparisons: ${comparisons}`,
    text, pattern,
    textIndex: -1,
    patternIndex: -1,
    comparisons,
    found: [...found],
    algo: 'naive',
    result: found.length > 0 ? found.join(', ') : 'not found'
  });

  return steps;
};

// ============ STRING 2: KMP ============

const computeLPS = (pattern) => {
  const lps = new Array(pattern.length).fill(0);
  let len = 0;
  let i = 1;
  while (i < pattern.length) {
    if (pattern[i] === pattern[len]) {
      len++;
      lps[i] = len;
      i++;
    } else {
      if (len !== 0) len = lps[len - 1];
      else { lps[i] = 0; i++; }
    }
  }
  return lps;
};

export const generateKMPSteps = (input) => {
  const parts = input.split('|').map(s => s.trim());
  const pattern = parts[0] || '';
  const text = parts[1] || '';
  const m = pattern.length;
  const n = text.length;
  const lps = computeLPS(pattern);
  const steps = [];
  const found = [];
  let comparisons = 0;

  steps.push({
    line: 3,
    description: `KMP: pattern="${pattern}", text="${text}". LPS = [${lps.join(', ')}]`,
    text, pattern,
    textIndex: -1,
    patternIndex: -1,
    comparisons,
    found: [],
    lps,
    algo: 'kmp'
  });

  let i = 0, j = 0;
  while (i < n) {
    comparisons++;
    const match = text[i] === pattern[j];
    steps.push({
      line: 7,
      description: `Compare text[${i}]='${text[i]}' with pattern[${j}]='${pattern[j]}' → ${match ? 'match' : 'MISMATCH'}`,
      text, pattern,
      textIndex: i - j,
      patternIndex: j,
      matchPos: i,
      comparisons,
      found: [...found],
      matchStatus: match ? 'match' : 'mismatch',
      lps,
      algo: 'kmp'
    });

    if (match) {
      i++; j++;
      if (j === m) {
        found.push(i - j);
        steps.push({
          line: 10,
          description: `Full match at index ${i - j}!`,
          text, pattern,
          textIndex: i - j,
          patternIndex: m - 1,
          comparisons,
          found: [...found],
          matchStatus: 'found',
          lps,
          algo: 'kmp'
        });
        j = lps[j - 1];
      }
    } else if (j > 0) {
      steps.push({
        line: 14,
        description: `Mismatch: skip using LPS. j = lps[${j - 1}] = ${lps[j - 1]}`,
        text, pattern,
        textIndex: i - j + lps[j - 1],
        patternIndex: lps[j - 1],
        comparisons,
        found: [...found],
        lps,
        algo: 'kmp'
      });
      j = lps[j - 1];
    } else {
      i++;
    }
  }

  steps.push({
    line: 18,
    description: found.length > 0
      ? `Complete! Found at: [${found.join(', ')}]. Comparisons: ${comparisons} (vs naive would be up to ${(n - m + 1) * m})`
      : `Complete! Not found. Comparisons: ${comparisons}`,
    text, pattern,
    textIndex: -1,
    patternIndex: -1,
    comparisons,
    found: [...found],
    lps,
    algo: 'kmp',
    result: found.length > 0 ? found.join(', ') : 'not found'
  });

  return steps;
};

// ============ STRING 3: RABIN-KARP ============

export const generateRabinKarpSteps = (input) => {
  const parts = input.split('|').map(s => s.trim());
  const pattern = parts[0] || '';
  const text = parts[1] || '';
  const m = pattern.length;
  const n = text.length;
  const steps = [];
  const found = [];
  const BASE = 31;
  const MOD = 101;

  // Compute pattern hash
  let patternHash = 0;
  for (let k = 0; k < m; k++) {
    patternHash = (patternHash * BASE + pattern.charCodeAt(k)) % MOD;
  }

  // Initial window hash
  let windowHash = 0;
  for (let k = 0; k < m && k < n; k++) {
    windowHash = (windowHash * BASE + text.charCodeAt(k)) % MOD;
  }

  // Precompute BASE^(m-1) % MOD
  let h = 1;
  for (let k = 0; k < m - 1; k++) h = (h * BASE) % MOD;

  steps.push({
    line: 3,
    description: `Rabin-Karp: pattern hash = ${patternHash}`,
    text, pattern,
    textIndex: -1,
    patternIndex: -1,
    patternHash,
    windowHash: null,
    comparisons: 0,
    found: [],
    algo: 'rabin-karp'
  });

  let comparisons = 0;

  for (let i = 0; i <= n - m; i++) {
    steps.push({
      line: 7,
      description: `Window at ${i}: "${text.substring(i, i + m)}", hash = ${windowHash}`,
      text, pattern,
      textIndex: i,
      patternIndex: 0,
      patternHash,
      windowHash,
      comparisons,
      found: [...found],
      algo: 'rabin-karp'
    });

    if (windowHash === patternHash) {
      comparisons++;
      const isMatch = text.substring(i, i + m) === pattern;
      steps.push({
        line: 9,
        description: `Hash matches! Verify chars: "${text.substring(i, i + m)}" == "${pattern}" → ${isMatch ? 'MATCH' : 'FALSE POSITIVE'}`,
        text, pattern,
        textIndex: i,
        patternIndex: 0,
        patternHash,
        windowHash,
        comparisons,
        found: [...found],
        matchStatus: isMatch ? 'found' : 'mismatch',
        algo: 'rabin-karp'
      });
      if (isMatch) {
        found.push(i);
      }
    }

    // Roll the hash
    if (i < n - m) {
      const newHash = ((windowHash - text.charCodeAt(i) * h) * BASE + text.charCodeAt(i + m)) % MOD;
      windowHash = ((newHash % MOD) + MOD) % MOD;
    }
  }

  steps.push({
    line: 15,
    description: found.length > 0
      ? `Complete! Found at: [${found.join(', ')}]. Full-string comparisons: ${comparisons}`
      : `Complete! Not found. Full-string comparisons: ${comparisons}`,
    text, pattern,
    textIndex: -1,
    patternIndex: -1,
    patternHash,
    windowHash: null,
    comparisons,
    found: [...found],
    algo: 'rabin-karp',
    result: found.length > 0 ? found.join(', ') : 'not found'
  });

  return steps;
};
